export function groupVoiceTranscript(fragments = []) {
    const turns = [];
    const seen = new Set();
    for (const fragment of fragments) {
        if (!['user', 'assistant'].includes(fragment.speaker) || typeof fragment.text_fragment !== 'string' || !fragment.text_fragment) continue;
        if (fragment.event_id && seen.has(fragment.event_id)) continue;
        if (fragment.event_id) seen.add(fragment.event_id);
        const previous = turns.at(-1);
        if (previous && previous.speaker === fragment.speaker && previous.context_version === fragment.context_version) {
            previous.text_fragment += fragment.text_fragment;
        } else turns.push({ ...fragment });
    }
    return turns;
}

export function createLocalVoiceRecording({ onReady, onError, devices = navigator.mediaDevices, Recorder = window.MediaRecorder }) {
    let stream;
    let recorder;
    let timer;
    let stopped = false;
    let discarded = false;
    let started = false;
    let bytes = 0;
    let parts = [];
    const stopTracks = () => { clearTimeout(timer); stream?.getTracks().forEach((track) => track.stop()); };
    const stop = ({ discard = false } = {}) => {
        stopped = true;
        discarded ||= discard;
        stopTracks();
        if (discarded) parts = [];
        if (recorder && recorder.state !== 'inactive') recorder.stop();
    };
    const start = async ({ consent } = {}) => {
        if (consent !== true) throw new Error('Explicit local-recording consent is required');
        if (started || stopped) throw new Error('Start a new recording explicitly');
        const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) => Recorder?.isTypeSupported?.(type));
        if (!mimeType || !devices?.getUserMedia) throw new Error('Local audio recording is not supported by this browser');
        started = true;
        try {
            stream = await devices.getUserMedia({ audio: true });
            if (stopped) { stopTracks(); return; }
            recorder = new Recorder(stream, { mimeType, audioBitsPerSecond: 64000 });
            recorder.ondataavailable = ({ data }) => {
                if (discarded || !data?.size) return;
                if (bytes + data.size > 10 * 1024 * 1024) { stop(); return; }
                parts.push(data); bytes += data.size;
            };
            recorder.onstop = () => {
                stopTracks();
                if (!discarded) {
                    if (bytes) onReady?.(new Blob(parts, { type: mimeType }));
                    else onError?.('No local audio was captured');
                }
                parts = [];
            };
            recorder.onerror = () => { onError?.('Local audio recording failed'); stop({ discard: true }); };
            recorder.start(250);
            timer = setTimeout(() => stop(), 180000);
        } catch (error) { stop({ discard: true }); throw error; }
    };
    return { start, stop };
}

export function createVoiceConnection({ request, onEvent, onState, onEnded, onError,
    devices = navigator.mediaDevices, Peer = window.RTCPeerConnection, AudioPlayer = window.Audio,
    heartbeatEveryMs = 10000 }) {
    let peer;
    let channel;
    let microphone;
    let audio;
    let sessionId;
    let heartbeat;
    let startupTimer;
    let ended = false;
    let closing;
    let iceCancel;
    const bounded = async (path, options, milliseconds) => {
        const controller = new AbortController();
        let timer;
        try {
            return await Promise.race([request(path, { ...options, signal: controller.signal }), new Promise((resolve, reject) => {
                timer = setTimeout(() => { controller.abort(); reject(new Error('Voice request timed out. Recover the saved session before retrying.')); }, milliseconds);
            })]);
        } finally { clearTimeout(timer); }
    };
    const cleanup = () => {
        clearInterval(heartbeat);
        clearTimeout(startupTimer);
        iceCancel?.();
        microphone?.getTracks().forEach((track) => track.stop());
        channel?.close();
        peer?.close();
        if (audio) { audio.pause(); audio.srcObject = null; }
    };
    const end = () => {
        if (closing) return closing;
        ended = true;
        microphone?.getTracks().forEach((track) => { track.enabled = false; });
        microphone?.getTracks().forEach((track) => track.stop());
        iceCancel?.();
        onState('Finishing');
        closing = Promise.resolve().then(async () => {
            try {
                if (sessionId) await bounded(`/sessions/${sessionId}/end`, { method: 'POST', body: {}, keepalive: true }, 35000);
            } finally { cleanup(); onState('Ended'); onEnded?.(); }
        });
        return closing;
    };
    const fail = (error) => {
        onError(error.message || 'Voice connection failed');
        void end().catch(() => {});
    };
    async function start({ preview = false, context, continuationOf } = {}) {
        try {
            if (!devices?.getUserMedia || !Peer) throw new Error('This browser does not support microphone voice calls. Use the questionnaire to enter notes.');
            onState('Requesting microphone');
            microphone = await devices.getUserMedia({ audio: true });
            if (ended) { cleanup(); return; }
            peer = new Peer();
            audio = new AudioPlayer();
            audio.autoplay = true;
            peer.addEventListener('track', ({ track }) => {
                if (ended) return;
                audio.srcObject = new MediaStream([track]);
                audio.play().catch(() => onState('Playback blocked'));
            });
            peer.addEventListener('connectionstatechange', () => {
                if (ended) return;
                if (['failed', 'closed'].includes(peer.connectionState)) fail(new Error('Voice disconnected. Saved transcripts remain available.'));
                if (peer.connectionState === 'disconnected') onState('Reconnecting');
                if (peer.connectionState === 'connected') onState('Listening');
            });
            microphone.getAudioTracks().forEach((track) => peer.addTrack(track, microphone));
            channel = peer.createDataChannel('oai-events');
            channel.addEventListener('message', ({ data }) => {
                if (ended) return;
                let event;
                try { event = JSON.parse(data); } catch { fail(new Error('Invalid voice event')); return; }
                onEvent(event);
                if (event.type === 'session.started') { clearTimeout(startupTimer); onState('Listening'); }
                if (event.type === 'session.input_transcript.delta') onState('Listening');
                if (event.type === 'session.output_transcript.delta') onState('Assistant speaking');
                if (event.type === 'session.closed') void end().catch(() => {});
                if (event.type === 'error') onError('The voice service rejected a command. End and resume if the problem persists.');
            });
            channel.addEventListener('close', () => {
                if (!ended) fail(new Error('Voice event connection closed. Saved transcripts remain available.'));
            });
            onState('Connecting');
            await peer.setLocalDescription(await peer.createOffer());
            if (ended) { cleanup(); return; }
            if (peer.iceGatheringState !== 'complete') {
                await new Promise((resolve, reject) => {
                    const timer = setTimeout(() => { peer.removeEventListener('icegatheringstatechange', changed); reject(new Error('Microphone connection timed out')); }, 10000);
                    iceCancel = () => {
                        clearTimeout(timer); peer.removeEventListener('icegatheringstatechange', changed); resolve(); iceCancel = null;
                    };
                    function changed() {
                        if (peer.iceGatheringState !== 'complete') return;
                        clearTimeout(timer);
                        peer.removeEventListener('icegatheringstatechange', changed);
                        iceCancel = null;
                        resolve();
                    }
                    peer.addEventListener('icegatheringstatechange', changed);
                    changed();
                });
            }
            if (ended) { cleanup(); return; }
            const result = await bounded('/sessions', { method: 'POST', body: { sdp: peer.localDescription.sdp,
                request_id: crypto.randomUUID(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, preview,
                ...(!preview && context ? { context } : {}), ...(continuationOf ? { continuation_of: continuationOf } : {}) } }, 40000);
            sessionId = result.id;
            if (ended) {
                await bounded(`/sessions/${sessionId}/end`, { method: 'POST', body: {} }, 35000);
                cleanup(); return;
            }
            onEvent({ type: 'monsurv.session.created', ...result });
            heartbeat = setInterval(() => {
                bounded(`/sessions/${sessionId}/heartbeat`, { method: 'POST', body: {} }, 8000).then((status) => {
                    onEvent({ type: 'monsurv.allowance', ...status });
                }).catch(fail);
            }, heartbeatEveryMs);
            startupTimer = setTimeout(() => fail(new Error('Voice startup was not confirmed. Recover the saved session before retrying.')), 20000);
            await peer.setRemoteDescription({ type: 'answer', sdp: result.transport.sdp });
        } catch (error) {
            const message = error.name === 'NotAllowedError'
                ? 'Microphone permission was denied. Change microphone permission in your browser site settings, then try again.' : error.message;
            onError(message);
            await end().catch(() => {});
            throw new Error(message);
        }
    }
    return { start, end, mute: (muted) => {
        microphone?.getAudioTracks().forEach((track) => { track.enabled = !muted; });
        onState(muted ? 'Muted - session remains billable' : 'Listening');
    }, play: () => audio?.play(), getSessionId: () => sessionId };
}